import { useState, useEffect, useCallback } from 'react';
import { residentApi } from '../../../services/residentApi';
import { ContactWithDetails, Contact } from '../../../../crm/components/contacts/types';
import { useLoadingState } from '../../../../hooks/useLoadingState';

export const useResidentContacts = () => {
  const [contacts, setContacts] = useState<ContactWithDetails[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);

  const fetchContacts = useCallback(async () => {
    startLoading('fetch-contacts', 'Loading contacts...');
    setError(null);
    try {
      const response = await residentApi.getContacts();
      setContacts(response.data || []);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setContacts([]);
      } else {
        setError(err.message || 'Failed to fetch contacts');
      }
    } finally {
      stopLoading('fetch-contacts');
    }
  }, [startLoading, stopLoading]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const createContact = useCallback(async (contactData: Omit<Contact, 'id' | 'dateCreated' | 'dateUpdated' | 'whoUpdated'>) => {
    startLoading('create-contact', 'Creating contact...');
    setError(null);
    try {
      const response = await residentApi.createContact(contactData);
      await fetchContacts();
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create contact';
      setError(errorMessage);
      throw err;
    } finally {
      stopLoading('create-contact');
    }
  }, [startLoading, stopLoading, fetchContacts]);

  const updateContact = useCallback(async (id: number, contactData: Partial<Contact>) => {
    startLoading('update-contact', 'Updating contact...');
    setError(null);
    try {
      const response = await residentApi.updateContact(id, contactData);
      await fetchContacts();
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update contact';
      setError(errorMessage);
      throw err;
    } finally {
      stopLoading('update-contact');
    }
  }, [startLoading, stopLoading, fetchContacts]);

  const deleteContact = useCallback(async (id: number) => {
    startLoading('delete-contact', 'Deleting contact...');
    setError(null);
    try {
      await residentApi.deleteContact(id);
      await fetchContacts();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete contact';
      setError(errorMessage);
      throw err;
    } finally {
      stopLoading('delete-contact');
    }
  }, [startLoading, stopLoading, fetchContacts]);

  const refetch = useCallback(() => {
    fetchContacts();
  }, [fetchContacts]);

  return {
    contacts,
    error,
    globalLoading,
    loadingMessage,
    refetch,
    createContact,
    updateContact,
    deleteContact,
  };
};

